/**
 * **剪线 / 静音连线：划痕的 DOM 侧** —— 从 Playground.vue 抽出来。
 *
 * ## 这个文件负责什么
 *
 * 几何计算在 `graph/cutGeometry.ts`（纯函数、有单测），状态在状态机里 ——
 * 这里只做**和 DOM 打交道**的那一层：
 *
 *   `sampleEdgePath`        把一条边的真实 SVG 路径采样成折线点
 *   `sampleEdgePathFine`    同上但更密 —— 给「把接通节点放到光标附近」用
 *   `collectEdgeCandidates` 当前图上所有可被剪的边
 *   `toFlowPoints`          屏幕坐标 → 画布坐标
 *   `onLassoDown`           划痕开始：记起点、把指针交给状态机
 *   `lassoPoints`           模板画覆盖层用的折线点
 *
 * ## ⚠️ 抽出来的方式：函数体逐字不动
 */
import { computed } from 'vue'
import {
  findCutTargets,
  lassoModeOf,
  polylineToSvgPoints,
  simplifyPolyline,
} from '@/graph/cutGeometry'
import type { CutCandidate, Point } from '@/graph/cutGeometry'
import { FLOW_DOM } from '@/graph/graphDom'
import { viewportOf } from '@/graph/flowStore'
import type { FlowStoreApi } from '@/graph/flowStore'
import { gestureWantsInputs } from '@/graph/canvasGesture'

export interface CutLassoCtx {
  flowStore: { value: FlowStoreApi | null }
  graphRef: { value: HTMLElement | null }
  /**
   * 在**当前焦点 Area 的画布**里查全部匹配元素。
   *
   * ⚠️ 不要在这里 `document.querySelectorAll('.vue-flow__edge-path')`：
   * 两个节点图窗口看的是同一份文档，边 id 相同，document 级查询会把
   * **两个窗口的边都收进来** —— 剪线时会剪到看不见的那个窗口的副本，
   * 而且候选点算重。注入进来是为了让「查哪张画布」由宿主决定。
   */
  queryAllGraph: <T extends Element = Element>(selector: string) => T[]
  /** 划痕路径（状态机派生，声明得比这段早） */
  lassoPath: { value: Point[] }
  /** 手势事件入口（声明得比这段晚，闭包注入） */
  sendGesture: (event: unknown) => void
}

export function useCutLasso(ctx: CutLassoCtx) {
  const { flowStore, graphRef, queryAllGraph, lassoPath, sendGesture } = ctx

/* ── 剪线 / 静音连线：划痕的 DOM 侧（几何与状态都在别处） ─────── */

/**
 * 采样一条边的真实路径点（画布坐标）。
 *
 * 用 DOM 上真实的 path 而不是自己算贝塞尔 —— 所见即所得：
 * 划到哪条线就该删哪条，不能因为曲线估算不准而误伤旁边的。
 */
function sampleEdgePath(path: SVGPathElement, stepSize = 12, maxSteps = 24): Point[] {
  if (typeof path.getTotalLength !== 'function') return []
  const total = path.getTotalLength()
  if (!total) return []
  const steps = Math.max(2, Math.min(maxSteps, Math.ceil(total / stepSize)))
  const points: Point[] = []
  for (let i = 0; i <= steps; i += 1) {
    const p = path.getPointAtLength((total * i) / steps)
    points.push({ x: p.x, y: p.y })
  }
  return points
}

/**
 * **密集采样**，给「把接通节点放到光标附近」用。
 *
 * ⚠️ 为什么不能复用上面那份默认密度：
 * 双击线条时，接通节点落在**离光标最近的那个采样点**上。
 * 默认密度是每 ~12 画布单位一个点、最多 25 个 —— 对一条绕来绕去的长线，
 * 相邻采样点可能隔着几十像素，于是节点会明显**偏在光标一侧**，
 * 看起来就是「没有加在我双击的位置」。
 *
 * `getPointAtLength` 是 DOM 调用，比较贵，所以**只给双击用**
 * （一次一个事件）；剪线划痕那条路径仍然用默认密度 —— 它在
 * `pointermove` 里对**所有**边跑一遍，加密会直接卡住拖拽。
 */
function sampleEdgePathFine(path: SVGPathElement): Point[] {
  return sampleEdgePath(path, 3, 96)
}

function collectEdgeCandidates(): CutCandidate<string>[] {
  const out: CutCandidate<string>[] = []
  /* ⚠️ 选择器从 `graphDom.ts` 取（引擎的 DOM 契约） */
  for (const el of queryAllGraph(`.${FLOW_DOM.edgePath}`)) {
    // 引擎把边 id 放在外层 <g class="…edge" data-id="…"> 上
    const id = el.closest(`.${FLOW_DOM.edge}`)?.getAttribute('data-id')
    if (!id) continue
    const points = sampleEdgePath(el as SVGPathElement)
    if (points.length > 0) out.push({ key: id, points })
  }
  return out
}

/** 屏幕坐标（相对画布容器）→ 画布坐标 */
function toFlowPoints(points: readonly Point[]): Point[] {
  const vp = viewportOf(flowStore.value)
  return points.map((p) => ({
    x: (p.x - vp.x) / vp.zoom,
    y: (p.y - vp.y) / vp.zoom,
  }))
}

function onLassoDown(ev: PointerEvent) {
  /*
   * ⚠️ 判断抽在 `lassoModeOf`（纯函数、`verify:gestures` 有断言）：
   * 这段原来是一行就地三元表达式，于是"哪个修饰键对应哪种划痕"
   * 只能靠手动试 —— 而用户报"其他快捷键不能用"时，这类**手势修饰键**
   * 正是最难自己发现坏没坏的一类。
   */
  const mode = lassoModeOf({ altKey: ev.altKey, shiftKey: ev.shiftKey, button: ev.button })
  if (!mode) return

  // ★ capture 阶段拦下 —— 不拦的话图引擎会同时开始框选 / 拖节点
  ev.stopPropagation()
  ev.preventDefault()

  // 剩下的交给状态机：它会挂上指针监听、跟踪路径、松手时判定命中
  sendGesture({
    type: 'lassoStart',
    mode,
    point: { x: ev.clientX, y: ev.clientY },
  })
}

/** 划痕覆盖层的 polyline points（相对画布容器） */
const lassoPoints = computed(() => {
  const host = graphRef.value
  if (!host || lassoPath.value.length === 0) return ''
  const rect = host.getBoundingClientRect()
  return polylineToSvgPoints(
    lassoPath.value.map((p) => ({ x: p.x - rect.left, y: p.y - rect.top })),
  )
})


  return {
    sampleEdgePath,
    sampleEdgePathFine,
    collectEdgeCandidates,
    toFlowPoints,
    onLassoDown,
    lassoPoints,
  }
}
